import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Drawer, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as ExcelIcon } from 'src/Images/icons/common/excel.svg';
import { ReactComponent as Refresh } from 'src/Images/icons/common/refresh.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import Table from 'src/components/Vehicle/List/Table';
import TableRightDrawer from 'src/components/Vehicle/List/TableRightDrawer';
import Breadcrumb from 'src/components/Breadcrumb';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import getColorCode from 'src/utils/GetThemeColor';
import usePageTitle from 'src/utils/SetPageTitle';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import { getTransporterList } from 'src/redux/Actions/Settings';
import 'src/style/tableList.css';
import { useLocation } from 'react-router-dom';
import { loadExcelExportModules } from 'src/utils/excelExport';
import TransporterTableContent from 'src/components/VehicleTrackingReport/TransporterTableContent';

const DEFAULT_FIELDS = ['1', '2', '3', '4', '5', '6'];
const PREFERENCE_KEY = 'savedTransporterColumns';

const emptyFilters = {
  createdStartDate: '',
  createdEndDate: '',
  modifiedStartDate: '',
  modifiedEndDate: '',
};

export default function TransporterList() {
  usePageTitle('Transporters');
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:767px)');
  const authReducer = useSelector((state) => state.AuthReducer);
  const transporterListData = useSelector(
    (state) => state.SettingsReducer.transporterList,
  );
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer?.userData?.organisations?.id;

  const [pageNumber, setPageNumber] = useState(1);
  const [paginationBatchSize, setPaginationBatchSize] = useState(10);
  const [showFunnel, setShowFunnel] = useState(false);
  const [openOrganizer, setOpenOrganizer] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [formData, setFormData] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const savedPreferences = useMemo(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PREFERENCE_KEY));
      return {
        selected: saved?.selected || DEFAULT_FIELDS,
        order: saved?.order || [],
      };
    } catch {
      return { selected: DEFAULT_FIELDS, order: [] };
    }
  }, []);
  const [selectedFieldFilter, setSelectedFieldFilter] = useState(
    savedPreferences.selected,
  );
  const [draggedFieldOrder, setDraggedFieldOrder] = useState(
    savedPreferences.order,
  );

  const fieldFilter = transporterListData?.fieldMapping || [];
  const rows = transporterListData?.transporters || [];
  const totalPages = transporterListData?.totalPages || 1;
  const selectedFields = transporterListData?.selectedFields || [];
  const effectiveFieldOrder =
    draggedFieldOrder.length > 0 ? draggedFieldOrder : selectedFieldFilter;

  const tableHeaders = useMemo(
    () =>
      selectedFields.map((field, index) => ({
        ...field,
        columnId: field.value || `col-${index}`,
      })),
    [selectedFields],
  );

  useSetBreadcrumbData(location.pathname, 'Transporters');

  const debouncedSearch = useCallback(() => {
    const timer = setTimeout(() => {
      setSearchInput(searchText);
      setPageNumber(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  React.useEffect(debouncedSearch, [debouncedSearch]);

  const fetchTransporterList = useCallback(() => {
    if (!tenantOrganisationId) return;
    const params = new URLSearchParams();

    if (appliedFilters.createdStartDate) {
      params.append('created_date_start', appliedFilters.createdStartDate);
    }
    if (appliedFilters.createdEndDate) {
      params.append('created_date_end', appliedFilters.createdEndDate);
    }
    if (appliedFilters.modifiedStartDate) {
      params.append('modified_start_date', appliedFilters.modifiedStartDate);
    }
    if (appliedFilters.modifiedEndDate) {
      params.append('modified_end_date', appliedFilters.modifiedEndDate);
    }

    dispatch(
      getTransporterList(
        tenantOrganisationId,
        pageNumber,
        searchInput,
        effectiveFieldOrder,
        paginationBatchSize,
        params.toString(),
      ),
    );
  }, [
    appliedFilters,
    dispatch,
    effectiveFieldOrder,
    pageNumber,
    paginationBatchSize,
    refreshKey,
    searchInput,
    tenantOrganisationId,
  ]);

  React.useEffect(() => {
    fetchTransporterList();
  }, [fetchTransporterList]);

  React.useEffect(() => {
    localStorage.setItem(
      PREFERENCE_KEY,
      JSON.stringify({
        selected: selectedFieldFilter,
        order: draggedFieldOrder,
      }),
    );
  }, [draggedFieldOrder, selectedFieldFilter]);

  const toggleOrganizer = (open) => () => setOpenOrganizer(open);

  const handleSelectedFieldFilter = (event, value) => {
    setSelectedFieldFilter((current) =>
      event.target.checked
        ? [...new Set([...current, value])]
        : current.filter((field) => field !== value),
    );
  };

  const handleDateFilterChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleResetFilter = () => {
    setFormData(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPageNumber(1);
  };

  const handleApplyFilter = () => {
    setAppliedFilters(formData);
    setPageNumber(1);
    if (isMobile) setShowFunnel(false);
  };

  const hasUnappliedFilters = () =>
    Object.keys(formData).some((key) => formData[key] !== appliedFilters[key]);

  const exportToExcel = async () => {
    if (!rows.length) return;
    const { utils, write, saveAs } = await loadExcelExportModules();
    const excelRows = rows.map((row) => {
      const excelRow = {};
      tableHeaders.forEach((header) => {
        const cellValue = row[header.value];
        if (Array.isArray(cellValue)) {
          excelRow[header.label] = cellValue.length
            ? cellValue.join(', ')
            : '-';
        } else if (cellValue && typeof cellValue === 'object') {
          excelRow[header.label] = cellValue.name ?? '-';
        } else if (typeof cellValue === 'boolean') {
          excelRow[header.label] = cellValue ? 'Yes' : 'No';
        } else {
          excelRow[header.label] = cellValue ?? '-';
        }
      });
      return excelRow;
    });
    const worksheet = utils.json_to_sheet(excelRows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Transporters');
    const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      `Transporters_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const handleInputChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) setPageNumber(pageNumber - 1);
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) setPageNumber(pageNumber + 1);
  };

  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;

  return (
    <div className="div-flex-column" data-testid="transporter-page">
      <div className="page-layout div-flex-column">
        <div className="div-space-between slider-padding left-padding">
          <div className="div-flex-row cg-5">
            <Breadcrumb />
          </div>
          <Col xxl={2} xl={2} lg={4} md={5} sm={7} xs={7} className="p-0 m-0">
            <div className="input-wrapper">
              <input
                type="text"
                data-testid="transporter-search"
                className="form-input-section input-icon-remove"
                placeholder="Search transporter"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <span className="form-input-section-icon">
                <Search
                  className="icon-size-lg"
                  color={getColorCode(themeColor)}
                />
              </span>
            </div>
          </Col>
        </div>
        <div className="div-space-between border-bottom slider-padding">
          <span className="text-xxl text-bold left-padding">Transporters</span>
          <div className="div-flex-row div-align-center cg-20">
            <div className="div-flex-row cg-20 div-align-center">
              <div className="list-container div-flex-column rg-5">
                <CustomToolTip title="Filter" placement="bottom">
                  <button
                    data-testid="filter-open-btn"
                    className="icon-btn-none"
                    aria-label="filter"
                    onClick={() => {
                      setShowFunnel((current) => !current);

                      if (isMobile) {
                        setShowFunnel(true);
                      }
                    }}
                  >
                    <Funnel
                      className="icon-size-lg"
                      color={showFunnel ? getColorCode(themeColor) : '#9F9F9F'}
                    />
                  </button>
                </CustomToolTip>
              </div>
              <div className="button-container">
                <CustomToolTip title="Organiser" placement="bottom">
                  <button
                    data-testid="organizer-open-btn"
                    className="icon-btn-none"
                    aria-label="organizer"
                    onClick={toggleOrganizer(true)}
                  >
                    <BulletPoints className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              <Drawer
                anchor="right"
                open={openOrganizer}
                onClose={toggleOrganizer(false)}
                ModalProps={{ keepMounted: false }}
                sx={{ zIndex: 2 }}
              >
                <div className="right-drawer-component-small">
                  <TableRightDrawer
                    toggleDrawer={toggleOrganizer}
                    fieldFilter={fieldFilter}
                    selectedFieldFilter={selectedFieldFilter}
                    fieldOrder={effectiveFieldOrder}
                    setDraggedFieldOrder={setDraggedFieldOrder}
                    handleSelectedFieldFilter={handleSelectedFieldFilter}
                  />
                </div>
              </Drawer>
              <div className="button-container">
                <CustomToolTip title="Export Excel" placement="bottom">
                  <button
                    aria-label="export excel"
                    className="icon-btn-none"
                    onClick={exportToExcel}
                  >
                    <ExcelIcon className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              {!isMobile && (
                <CustomToolTip title="Refresh" placement="bottom">
                  <button
                    aria-label="refresh"
                    className="icon-btn-none"
                    onClick={() => setRefreshKey((current) => current + 1)}
                  >
                    <Refresh className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              )}
            </div>
            {!isMobile && (
              <select
                value={paginationBatchSize}
                onChange={(event) => {
                  setPaginationBatchSize(Number(event.target.value));
                  setPageNumber(1);
                }}
                className="filter-input-section"
              >
                {[10, 20, 30, 40, 50, 100, 500, 1000].map((size) => (
                  <option value={size} key={size}>
                    {size}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        {isMobile && (
          <Drawer
            anchor="right"
            open={showFunnel}
            onClose={() => setShowFunnel(false)}
            ModalProps={{
              keepMounted: false,
            }}
            sx={{ zIndex: 2 }}
          >
            <div className="right-drawer-component-small">
              <div
                className="filter-list-item div-flex-row div-space-between div-align-center border-bottom"
                data-testid="filter-drawer-mobile"
              >
                <span className="text-sm text-semi-bold">Filter</span>
                {hasUnappliedFilters() && (
                  <div className="text-yellow text-xs text-semi-bold">
                    ⚠️ Unapplied Filters
                  </div>
                )}
              </div>
              <div className="div-flex-column div-space-between rg-20">
                <div className="filter-list-item-container-scroll div-flex-column">
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Created Start Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="createdStartDate"
                        value={formData.createdStartDate || ''}
                        onChange={handleDateFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Created End Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="createdEndDate"
                        value={formData.createdEndDate || ''}
                        onChange={handleDateFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Modified Start Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="modifiedStartDate"
                        value={formData.modifiedStartDate || ''}
                        onChange={handleDateFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Modified End Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="modifiedEndDate"
                        value={formData.modifiedEndDate || ''}
                        onChange={handleDateFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                  <button
                    className={`common-btn-outlined border-${themeColor}`}
                    onClick={handleResetFilter}
                    data-testid="filter-reset-btn-mobile"
                  >
                    Reset
                  </button>
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handleApplyFilter}
                    data-testid="filter-apply-btn-mobile"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </Drawer>
        )}
        <Row className="g-0">
          <Col
            xxl={showFunnel ? 10 : 12}
            xl={showFunnel ? 9 : 12}
            lg={showFunnel ? 8 : 12}
            md={showFunnel ? 8 : 12}
            sm={showFunnel ? 0 : 12}
            xs={showFunnel ? 0 : 12}
            className="border-bottom"
          >
            <Table
              headers={tableHeaders}
              minCellWidth={150}
              tableContent={<TransporterTableContent headers={tableHeaders} />}
            />
            <div className="footer-container">
              <div className="div-space-between div-align-center footer-content-for-page">
                <div className="div-flex-row cg-20 pagination-container div-align-center">
                  <div className="div-flex-row cg-10 div-align-center">
                    <button
                      data-testid="pagination-prev"
                      className="icon-btn-none"
                      onClick={handlePreviousPage}
                      aria-label="previous page"
                    >
                      <ArrowLeft
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                    <input
                      className="page-number-container input-icon-remove text-sm"
                      data-testid="pagination-input"
                      value={formattedPageNumber}
                      onChange={handleInputChange}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      max={totalPages}
                    />
                    <button
                      data-testid="pagination-next"
                      className="icon-btn-none"
                      onClick={handleNextPage}
                      aria-label="next page"
                    >
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                  </div>
                  <span className="text-grey text-sm">
                    Page {pageNumber} of {totalPages}
                  </span>
                </div>
              </div>
            </div>
          </Col>
          {showFunnel && !isMobile && (
            <Col
              xxl={showFunnel ? 2 : 0}
              xl={showFunnel ? 3 : 0}
              lg={showFunnel ? 4 : 0}
              md={showFunnel ? 4 : 0}
              sm={showFunnel ? 12 : 0}
              xs={showFunnel ? 12 : 0}
              className="border-left border-bottom"
            >
              <div
                className="filter-list-item div-flex-row div-space-between div-align-center border-bottom"
                data-testid="filter-drawer"
              >
                <span className="text-sm text-semi-bold">Filter</span>
                {hasUnappliedFilters() && (
                  <div className="text-yellow text-xs text-semi-bold">
                    ⚠️ Unapplied Filters
                  </div>
                )}
              </div>
              <div className="div-flex-column div-space-between rg-20">
                <div className="filter-list-item-container-scroll div-flex-column">
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Created Start Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="createdStartDate"
                        value={formData.createdStartDate || ''}
                        onChange={handleDateFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Created End Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="createdEndDate"
                        value={formData.createdEndDate || ''}
                        onChange={handleDateFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Modified Start Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="modifiedStartDate"
                        value={formData.modifiedStartDate || ''}
                        onChange={handleDateFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Modified End Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="modifiedEndDate"
                        value={formData.modifiedEndDate || ''}
                        onChange={handleDateFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                  <button
                    className={`common-btn-outlined border-${themeColor}`}
                    onClick={handleResetFilter}
                    data-testid="filter-reset-btn"
                  >
                    Reset
                  </button>
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handleApplyFilter}
                    data-testid="filter-apply-btn"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
}
